import { useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { MetaSearchOfferType } from 'actions/OfferActions'
import { fetchAccumulatedOfferById, fetchMetaOfferIds } from 'actions/OfferActions'
import { fetchMetaSearchOffer, getOfferWithDiscountKey, clearOffersWithDiscount } from 'actions/MarketingAction'
import getOfferListKey from 'lib/offer/offerListKey'
import type { SetOptional } from 'type-fest'
import { sortBy, without } from 'lib/array/arrayUtils'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { buildMetaSearchOfferListFilters } from 'lib/metasearch/metaSearchOfferListFilters'
import useAlternativeOffers from 'hooks/Offers/useAlternativeOffers'
import { OFFER_TYPE_HOTEL } from 'constants/offer'

export interface MetaSearchOfferListOptions {
  disabled?: boolean
  excludedIds?: Array<string>
  sortMode?: 'recommendation' | 'distance' | 'discount' | 'rating'
  /**
   * Bedbank propertyId from the metasearch URL. When set, the hook fetches
   * alternative-offers for that property and prepends the matched LE flash
   * offer ID to the returned list (slot 0 — "Same property, better deal").
   */
  propertyId?: string
}

export interface MetaSearchOfferListItem {
  offerId: string
  type?: MetaSearchOfferType
  checkIn?: string
  checkOut?: string
  discountedPackages?: App.FeedOperations['getOfferWithDiscount']['responses']['200']['content']['application/json']['result']['discountedPackages']
}

export interface MetaSearchOfferListState {
  items: Array<MetaSearchOfferListItem>
  fetching: boolean
}

const emptyOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: false,
  error: null,
}

const emptyResult: Array<MetaSearchOfferListItem> = []

function useMetaSearchOfferList(
  filters: App.MetaSearchObject | undefined,
  options: MetaSearchOfferListOptions = {},
): MetaSearchOfferListState {
  const { excludedIds, sortMode = 'recommendation', propertyId } = options

  const searchDisabled = options.disabled || !filters
  const dispatch = useAppDispatch()

  // Cache-or-fetch alternative offers for the bedbank property so we can
  // place the matched LE flash offer at index 0 of the returned list.
  // No-op when `propertyId` isn't set or the hook is disabled.
  const alternativeOffers = useAlternativeOffers(propertyId, 'bedbank', {
    disabled: searchDisabled,
  })
  const flashOfferId = alternativeOffers?.leOfferDetails.find(
    (detail) => detail.type === OFFER_TYPE_HOTEL,
  )?.offerId

  const regionCode = useAppSelector((state) => state.geo.currentRegionCode)

  // Cleanup offersWithDiscount state when unmounting
  useEffect(() => {
    return () => {
      dispatch(clearOffersWithDiscount())
    }
  }, [dispatch])

  // Build App.OfferListFilters from MetaSearchObject — shared with SSR via
  // `buildMetaSearchOfferListFilters` to guarantee the offer-list key matches
  // the SSR-populated slice (otherwise the client silently re-fetches).
  const offerListFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!filters) return undefined
    return buildMetaSearchOfferListFilters(filters, searchDisabled)
  }, [filters, searchDisabled])

  const listKey = useMemo(() => {
    if (offerListFilters) {
      return getOfferListKey(offerListFilters)
    }
    return ''
  }, [offerListFilters])

  useEffect(() => {
    if (searchDisabled || !offerListFilters) return

    dispatch(fetchMetaOfferIds(offerListFilters))
  }, [listKey, searchDisabled, dispatch, offerListFilters])

  const offerList = useAppSelector((state) => {
    if (searchDisabled) {
      return emptyDisabledOfferList
    }
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  // Background-prefetch every YMAL id so discount/rating sort tabs can sort
  // over a fully-populated `state.offer.offers` map and avoid re-shuffling
  // tiles as data streams in. Gate on the FIRST priority response landing in
  // the store: by then the head-warmed bulk request's 32ms accumulator
  // window has already flushed, so our dispatches start a fresh batch with
  // their own URL — no risk of merging into the priority URL and breaking
  // the head-warmup cache match. The thunk's own dedup guard then skips ids
  // that are already loaded or in-flight. Keyed by listKey so the prefetch
  // re-arms when the user navigates to a different metasearch property.
  const hasAnyLoadedOffer = useAppSelector((state) =>
    offerList.offerIds.some((id) => Boolean(state.offer.offers[id])),
  )
  const prefetchedListKeyRef = useRef<string | null>(null)

  const bypassRegionSettingId = useAppSelector((state) => state.marketing.bypassRegionSettingId)
  const bypassRegionExclusionOfferIds = useAppSelector((state) => state.marketing.bypassRegionExclusionOfferIds)

  useEffect(() => {
    if (searchDisabled || offerList.fetching || !hasAnyLoadedOffer) return
    if (prefetchedListKeyRef.current === listKey) return
    prefetchedListKeyRef.current = listKey

    offerList.offerIds.forEach((id) => {
      const bypassRegionFilter = !!bypassRegionSettingId && !bypassRegionExclusionOfferIds.includes(id)
      dispatch(fetchAccumulatedOfferById(id, undefined, undefined, bypassRegionFilter))
    })
  }, [searchDisabled, offerList.fetching, hasAnyLoadedOffer, offerList.offerIds, listKey, dispatch, bypassRegionSettingId, bypassRegionExclusionOfferIds])

  const offersWithDiscountState = useAppSelector((state) => state.offer.offersWithDiscount)
  const offersState = useAppSelector((state) => state.offer.offers)

  const metaSearchOfferTypes = offerList.metaSearchOfferTypes
  const metaSearchOfferDates = offerList.metaSearchOfferDates

  // Fetch offers with discount for pinned offers only (use server-provided default dates if available)
  useEffect(() => {
    if (searchDisabled || !filters || offerList.fetching || offerList.offerIds.length === 0) return

    offerList.offerIds.forEach((offerId) => {
      const offerType = metaSearchOfferTypes?.[offerId]
      if (offerType !== 'pinned') return

      const offerDates = metaSearchOfferDates?.[offerId]
      const checkIn = offerDates?.checkIn
      const checkOut = offerDates?.checkOut

      const key = getOfferWithDiscountKey(offerId, checkIn, checkOut)
      // Skip if already fetching or fetched for this key (offerId + dates)
      if (offersWithDiscountState[key]) return

      const params: { region: string, checkIn?: string, checkOut?: string } = { region: filters.region }
      if (checkIn) params.checkIn = checkIn
      if (checkOut) params.checkOut = checkOut

      dispatch(fetchMetaSearchOffer(offerId, params, regionCode))
    })
  }, [searchDisabled, filters, offerList.fetching, offerList.offerIds, metaSearchOfferTypes, metaSearchOfferDates, offersWithDiscountState, dispatch, regionCode])

  // Build the final sorted list with discounted packages. Flash is pinned at
  // index 0 across all sort modes — strip it from the sortable set, sort the
  // rest, then prepend flash. Idempotent w.r.t. SSR pre-merge: works whether
  // or not `fetchMetaOfferIds(filters, prependOfferId)` already placed flash
  // in `offerList.offerIds`.
  const result = useMemo((): Array<MetaSearchOfferListItem> => {
    if (searchDisabled || offerList.fetching) return emptyResult

    const filteredIds = excludedIds ? without(offerList.offerIds, ...excludedIds) : offerList.offerIds
    const nonFlashIds = flashOfferId ? filteredIds.filter((id) => id !== flashOfferId) : filteredIds

    const buildItem = (offerId: string): MetaSearchOfferListItem => {
      const offerType = metaSearchOfferTypes?.[offerId]
      const offerDates = metaSearchOfferDates?.[offerId]
      const checkIn = offerDates?.checkIn
      const checkOut = offerDates?.checkOut

      const key = getOfferWithDiscountKey(offerId, checkIn, checkOut)
      // Only pinned offers have discounted packages
      const discountData = offerType === 'pinned' ? offersWithDiscountState[key] : undefined
      return {
        offerId,
        type: offerType,
        checkIn,
        checkOut,
        discountedPackages: discountData?.discountedPackages,
      }
    }

    const nonFlashItems = nonFlashIds.map(buildItem)
    const flashItem = flashOfferId ? buildItem(flashOfferId) : undefined

    let sortedNonFlash: Array<MetaSearchOfferListItem>
    if (sortMode === 'recommendation') {
      sortedNonFlash = nonFlashItems
    } else {
      const mainLatitude = filters?.latitude
      const mainLongitude = filters?.longitude

      const enriched = nonFlashItems.map((item) => {
        const offer = offersState[item.offerId] as App.HotelOffer | undefined

        const offerLat = offer?.property?.latitude
        const offerLon = offer?.property?.longitude

        const distance = (
          typeof mainLatitude === 'number' &&
          typeof mainLongitude === 'number' &&
          typeof offerLat === 'number' &&
          typeof offerLon === 'number'
        )
          ? calculateDistance(mainLatitude, mainLongitude, offerLat, offerLon)
          : Number.POSITIVE_INFINITY

        const discount = offer?.lowestPricePackage?.defaultPricing?.discountPercent ?? 0
        const rating = offer?.property?.rating?.score ?? 0

        return { item, distance, discount, rating }
      })

      const sorted = sortBy(enriched, (a) => {
        if (sortMode === 'distance') return a.distance
        if (sortMode === 'discount') return a.discount
        if (sortMode === 'rating') return a.rating
        return 0
      }, sortMode === 'distance' ? 'asc' : 'desc')

      sortedNonFlash = sorted.map((e) => e.item)
    }

    return flashItem ? [flashItem, ...sortedNonFlash] : sortedNonFlash
  }, [searchDisabled, offerList.fetching, offerList.offerIds, excludedIds, flashOfferId, metaSearchOfferTypes, metaSearchOfferDates, offersWithDiscountState, offersState, sortMode, filters?.latitude, filters?.longitude])

  return {
    items: result,
    fetching: offerList.fetching,
  }
}

export default useMetaSearchOfferList
